import {
  IPayment,
  IPaymentCards,
  IPaymentPurchase,
  IPaymentMethodType
} from '@/interfaces/Payment';
import {
  ICheckoutCart,
  ICheckoutCartCustomerPaymentProfile,
  ICheckoutCartOfferConditionInstallment,
  ICheckoutCartCustomer,
  ICheckoutCartOfferPayment
} from '@/interfaces/Checkout';
import IPaymentMethod from '@/interfaces/Methods/Payment';

import { isValid } from '@/helpers/values';
import { applyCardMaskByBrand, cardBrandByBrandName } from '@/helpers/card';
import { EPaymentMethod, ECardStep, EPaymentMethodTypes, EEligibilityAction } from '@/helpers/enums';

import { IBankAccount } from '@/entities/Bank';

import BankSlipPaymentMethod from './Methods/Bankslip';
import CreditCardPaymentMethod from './Methods/CreditCard';
import StoreCreditPaymentMethod from './Methods/StoreCredit';
import WireTransferPaymentMethod from './Methods/WireTransfer';
import EmpiricusRewardsPaymentMethod from './Methods/EmpiricusRewards';
import PixPaymentMethod from './Methods/Pix';

import state from '@/app/module/state';

export class PaymentType<T> implements IPaymentMethodType<T> {
  type: EPaymentMethodTypes;
  methods: any[];
  isSelected: boolean = false;
  isAvailable: boolean = false;

  constructor(paymentType: T) {
    Object.assign(this, { ...paymentType });
  }

  public toggleSelection(status: boolean): void {
    this.isSelected = isValid(status) ? status : !this.isSelected;
  }

  public toggleAvailability(status: boolean): void {
    this.isAvailable = isValid(status) ? status : !this.isAvailable;
  }

  public get availableMethods(): IPaymentMethod[] {
    return this.methods.filter(method => method.isAvailable);
  }
}

export class Payment implements IPayment {
  cards: IPaymentCards = {
    main: null,
    options: new Array<ICheckoutCartCustomerPaymentProfile>(),
    installments: new Array<ICheckoutCartOfferConditionInstallment>(),
  };
  types = [
    new PaymentType({
      type: EPaymentMethodTypes.wireTransfer,
      methods: [new WireTransferPaymentMethod({ method: EPaymentMethod.wireTransfer })],
    }),
    new PaymentType({
      type: EPaymentMethodTypes.bankSlip,
      methods: [new BankSlipPaymentMethod({ method: EPaymentMethod.bankSlip })],
    }),
    new PaymentType({
      type: EPaymentMethodTypes.creditCard,
      methods: [new CreditCardPaymentMethod({ method: EPaymentMethod.creditCard })],
    }),
    new PaymentType({
      type: EPaymentMethodTypes.storeCredit,
      methods: [new StoreCreditPaymentMethod({ method: EPaymentMethod.storeCredit })],
    }),
    new PaymentType({
      type: EPaymentMethodTypes.empiricusRewards,
      methods: [new EmpiricusRewardsPaymentMethod({ method: EPaymentMethod.empiricusRewards })],
    }),
    new PaymentType({
      type: EPaymentMethodTypes.pix,
      methods: [new PixPaymentMethod({ method: EPaymentMethod.pix })],
    }),
  ];
  loading: boolean = false;
  eligibilityAction?: EEligibilityAction | null = EEligibilityAction.purchase;

  constructor(cart?: ICheckoutCart, customer?: ICheckoutCartCustomer, payment_structure?: ICheckoutCartOfferPayment) {
    if (cart && payment_structure) {
      this.eligibilityAction = cart.offer.eligibility && cart.offer.eligibility.eligible_action;

      this.buildCustomerCards(customer)
        .selectMainCustomerCard(cart)
        .selectDefaultCardStep()
        .selectDefaultMethod(payment_structure)
        .buildCardInstallments(payment_structure)
        .selectDefaultInstallment(payment_structure);
    }
  }

  private get isRenewal(): boolean {
    return Boolean(this.eligibilityAction && [EEligibilityAction.earlyRenewal, EEligibilityAction.lateRenewal]
      .includes(this.eligibilityAction));
  }

  public getPaymentMethodsByType(t: EPaymentMethodTypes): any {
    const type = this.types.find(<T>(type: IPaymentMethodType<T>) => {
      return type.type === t && type.isAvailable;
    });

    return type && type.availableMethods;
  }

  public getPaymentMethod(method: EPaymentMethod): IPaymentMethod | any {
    return this.allMethods.find((typeMethod: any) => typeMethod.method === method);
  }

  public get allMethods(): IPaymentMethod[] {
    return this.types
      .filter(<T>(type: IPaymentMethodType<T>) => type.isAvailable)
      .map(<T>(type: IPaymentMethodType<T>) => type.availableMethods)
      .reduce((acc, val) => acc.concat(val), []);
  }

  public hasMethod(method: EPaymentMethod): boolean {
    return this.allMethods.some((availableMethod: any) => availableMethod.method === method);
  }

  protected getCreditCardCondition(payment_structure: ICheckoutCartOfferPayment): any {
    return payment_structure && payment_structure.payment_conditions
      ? payment_structure.payment_conditions!.find((condition) => {
        return condition.payment_method_code === EPaymentMethod.creditCard;
      }) : null;
  }

  public selectPaymentType(selectedType: EPaymentMethodTypes): Payment {
    this.types.forEach(<T>(type: PaymentType<T>) => {
      type.toggleSelection(type.type === selectedType);
    });

    return this;
  }

  public togglePaymentTypeAvailability(selectedType: EPaymentMethodTypes): Payment {
    const type = this.types.find(<T>(type: PaymentType<T>) => type.type === selectedType);

    type && type.toggleAvailability(true);

    return this;
  }

  public get selectedPaymentType(): IPaymentMethodType<any> | any {
    let type = this.types.find(<T>(type: IPaymentMethodType<T>) => type.isSelected);

    if (state.paymentMethodSelected) {
      const findMethodSelected = this.types.find((type: any) => type.type === state.paymentMethodSelected);
      findMethodSelected && (findMethodSelected.isSelected = true);
      type = findMethodSelected;
    }

    return type && type.type;
  }

  public setPaymentMethodField(method: EPaymentMethod, field: string, value: any): Payment {
    this.types.forEach((<T>(type: IPaymentMethodType<T>) => {
      type.methods.forEach((typeMethod: any) => {
        if (typeMethod.method === method) {
          typeMethod[field] = typeof value === 'function' ? value(typeMethod) : value;
        }
      });
    }));

    return this;
  }

  public callPaymentMethodFunction(functionName: string, method: EPaymentMethod, value: any): Payment {
    this.types.forEach((<T>(type: IPaymentMethodType<T>) => {
      type.methods.forEach((typeMethod: any) => {
        if (typeMethod.method === method) {
          typeMethod[functionName](value);
        }
      });
    }));

    return this;
  }

  public selectPaymentMethod(selectedMethod: EPaymentMethod): Payment {
    const type = this.getTypeOfPaymentMethod(selectedMethod);

    type && this.selectPaymentType(type.type);

    this.types.forEach((<T>(type: IPaymentMethodType<T>) => {
      type.methods.forEach((typeMethod: any) => {
        typeMethod.toggleSelection(typeMethod.method === selectedMethod);
      });
    }));

    return this;
  }

  public selectPaymentCard({ cardId, lastDigits }: { cardId?: number, lastDigits?: number }): Payment {
    const card = this.cards.options.find((option: ICheckoutCartCustomerPaymentProfile) => {
      return cardId
        ? option.id === cardId
        : option.card_last_digits === lastDigits;
    });

    if (card) {
      this.setPaymentMethodField(EPaymentMethod.creditCard, 'card', card);
    }

    return this;
  }

  public selectPaymentSecondCard({ cardId, lastDigits }: { cardId?: number, lastDigits?: number }): Payment {
    const card = this.cards.options.find((option: ICheckoutCartCustomerPaymentProfile) => {
      return cardId
        ? option.id === cardId
        : option.card_last_digits === lastDigits;
    });

    if (card) {
      this.setPaymentMethodField(EPaymentMethod.creditCard, 'secondCard', card);
    }

    return this;
  }

  public selectPaymentCardInstallment(installmentNumber: number): Payment {
    const installment = this.cards.installments.find((installment: ICheckoutCartOfferConditionInstallment) => {
      return installment.number === installmentNumber;
    });

    if (installment) {
      this.setPaymentMethodField(EPaymentMethod.creditCard, 'installment', installment);
    }

    return this;
  }

  public selectPaymentCardStep(step: ECardStep): Payment {
    this.setPaymentMethodField(EPaymentMethod.creditCard, 'step', step);

    return this;
  }

  public get selectedPaymentMethod(): EPaymentMethod | any {
    if (state.paymentMethodSelected) {
      const findMethodSelected = this.allMethods.find((method: any) => method.type === state.paymentMethodSelected);
      findMethodSelected && (findMethodSelected.isSelected = true);
    }

    const method = this.allMethods.find((method: any) => method.isSelected);

    return method && method.method;
  }

  public paymentTypeIsAvailable(typeMethod: EPaymentMethodTypes): boolean {
    const type = this.types.find(<T>(type: PaymentType<T>) => type.type === typeMethod);

    return Boolean(type && type.isAvailable);
  }

  private buildCardInstallments(payment_structure: ICheckoutCartOfferPayment): any {
    const creditCard = this.getCreditCardCondition(payment_structure);
    const creditCardMethod = this.getPaymentMethod(EPaymentMethod.creditCard);

    if (creditCard && creditCard!.installments) {
      this.cards.installments = creditCard!.installments;
    }

    if (creditCardMethod && !creditCardMethod.cart_final_price) {
      this.cards.installments = [{
        amount: 0,
        number: 1,
      }];
    }

    return this;
  }

  private buildCustomerCards(customer?: ICheckoutCartCustomer): any {
    this.cards.options = customer && customer.payment_profiles
      ? customer.payment_profiles.map((payment_profile: ICheckoutCartCustomerPaymentProfile) => {
        const digits = `${payment_profile.card_first_digits}${payment_profile.card_last_digits}`;
        const creditCardMasked = applyCardMaskByBrand(payment_profile.card_flag, digits, '*');

        return {
          ...payment_profile,
          brand: cardBrandByBrandName(payment_profile.card_flag),
          text: `${creditCardMasked}`
        };
      }) : [];

    return this;
  }

  private selectMainCustomerCard(cart: ICheckoutCart): any {
    if (cart.early_renewal_contract && cart.early_renewal_contract.payment_profile_id) {
      this.cards.main = this.cards.options.find((card: any) => {
        return card.id === cart.early_renewal_contract!.payment_profile_id;
      });
    }

    this.cards.main = this.cards.options.find((card: any) => {
      return card.is_default;
    });

    const overrideSelected = this.cards.options.find((card: any) => {
      return card.selected;
    });

    if (overrideSelected) {
      this.cards.main = overrideSelected;
    }

    if (!this.cards.main && this.cards.options) {
      if (state.cardSelected) {
        this.cards.main = this.cards.options.find((option: any) => option.id === state.cardSelected);
      } else {
        this.cards.main = this.cards.options[0];
      }
    }

    this.setPaymentMethodField(EPaymentMethod.creditCard, 'card', this.cards.main);

    return this;
  }

  private selectDefaultCardStep(): any {
    let nextStep = ECardStep.select;

    nextStep = this.cards.main && state.stepFormSelected !== ECardStep.new
      ? ECardStep.select
      : ECardStep.new;

    this.setPaymentMethodField(EPaymentMethod.creditCard, 'step', nextStep);

    return this;
  }

  public getTypeOfPaymentMethod(toFindMethod: EPaymentMethod): IPaymentMethodType<any> | any {
    return this.types.find((type: any) => {
      return type.methods.find((method: any) => {
        return method.method === toFindMethod;
      });
    });
  }

  private selectDefaultMethod(payment_structure: ICheckoutCartOfferPayment): any {
    const structure = payment_structure;

    const conditionByMethod = (method: EPaymentMethod): any => {
      return structure.payment_conditions && structure.payment_conditions.find((condition: any) => condition.payment_method_code === method);
    };

    if (structure && structure.payment_conditions) {
      const availableMethods = structure.payment_conditions.map((condition: any) => condition.payment_method_code);

      Object.values(EPaymentMethod).forEach((method: EPaymentMethod) => {
        if (availableMethods.includes(method)) {
          const condition = conditionByMethod(method);
          const paymentType = this.getTypeOfPaymentMethod(method);

          condition && this.callPaymentMethodFunction('build', method, {
            id: condition.id,
            method,
            subtitle: condition.subtitle,
            discount_percentage: condition.discount_percentage,
            discount_amount: condition.payment_condition_discount_amount,
            cart_final_price: condition.cart_final_price,
          });

          // Disabling payment methods on renewal scenario
          if (process.env.VUE_APP_PAYMENT_METHODS_RENEWAL_DISABLED && this.isRenewal) {
            if (process.env.VUE_APP_PAYMENT_METHODS_RENEWAL_DISABLED.includes(method)) return;
          }

          this.callPaymentMethodFunction('toggleAvailability', method, true);
          paymentType && this.togglePaymentTypeAvailability(paymentType.type);
        }
      });
    }

    if (!this.selectedPaymentMethod) {
      if (this.hasMethod(EPaymentMethod.creditCard)) {
        this.selectPaymentMethod(EPaymentMethod.creditCard);
      } else if (this.hasMethod(EPaymentMethod.bankSlip)) {
        this.selectPaymentMethod(EPaymentMethod.bankSlip);
      } else {
        for (const method of Object.values(EPaymentMethod)) {
          if (this.hasMethod(method) && method !== EPaymentMethod.storeCredit) {
            this.selectPaymentMethod(method);
            break;
          }
        }
      }
    }

    return this;
  }

  public setCard(card: any): void {
    this.setPaymentMethodField(EPaymentMethod.creditCard, 'card', card);
  }

  public setSecondCard(card: any): void {
    this.setPaymentMethodField(EPaymentMethod.creditCard, 'secondCard', card);
  }

  public setBankAccount(account: IBankAccount): void {
    this.setPaymentMethodField(EPaymentMethod.wireTransfer, 'account', account);
  }

  public setCardPurchased(purchase: IPaymentPurchase): Payment {
    this.setPaymentMethodField(EPaymentMethod.creditCard, 'purchased', purchase);

    return this;
  }

  public setLoading(loading: boolean): Payment {
    this.loading = loading;

    return this;
  }

  private selectDefaultInstallment(payment_structure: ICheckoutCartOfferPayment): any {
    const creditCard = this.getCreditCardCondition(payment_structure);
    const creditCardMethod = this.getPaymentMethod(EPaymentMethod.creditCard);

    if (creditCard && creditCard.installments.length) {
      this.setPaymentMethodField(EPaymentMethod.creditCard, 'installment', creditCard.installments[creditCard.installments.length - 1]);
    } else if (creditCardMethod && !creditCardMethod.cart_final_price) {
      this.setPaymentMethodField(EPaymentMethod.creditCard, 'installment', this.cards.installments[0]);
    }

    return this;
  }

  public updatePaymentStructure(payment_structure: ICheckoutCartOfferPayment | void): Payment {
    if (payment_structure) {
      this.selectDefaultMethod(payment_structure)
        .buildCardInstallments(payment_structure)
        .selectDefaultInstallment(payment_structure);
    }

    return this;
  }

  public selectPaymentBankSlipInstallment(installmentNumber: number): Payment {
    this.setPaymentMethodField(EPaymentMethod.bankSlip, 'installment', installmentNumber);

    return this;
  }

  public setKoinFraudId(fraudId: string): Payment {
    this.setPaymentMethodField(EPaymentMethod.bankSlip, 'fraudId', fraudId);

    return this;
  }

  public selectSecondPaymentCard({ cardId, lastDigits }: { cardId?: number, lastDigits?: number }): Payment {
    const card = this.cards.options.find((option: ICheckoutCartCustomerPaymentProfile) => {
      return cardId
        ? option.id === cardId
        : option.card_last_digits === lastDigits;
    });

    if (card) {
      this.setPaymentMethodField(EPaymentMethod.creditCard, 'second_payment.card', card);
    }

    return this;
  }

  public selectAmountPaymentCard({ amount }: { amount?: number }): Payment {
    if (amount) {
      this.setPaymentMethodField(EPaymentMethod.creditCard, 'main_offer_payment_amount', amount);
    }

    return this;
  }

  public selectPaymentConditionId({ paymentMethod, offerPaymentConditionId }: { paymentMethod: EPaymentMethod, offerPaymentConditionId?: number }): Payment {
    if (offerPaymentConditionId) {
      this.setPaymentMethodField(paymentMethod, 'offer_payment_condition_id', offerPaymentConditionId);
    }

    return this;
  }

  public selectMainPaymentCondition({ mainPaymentCondition }: { mainPaymentCondition?: any }): Payment {
    if (mainPaymentCondition) {
      this.setPaymentMethodField(EPaymentMethod.creditCard, 'main_payment_condition', mainPaymentCondition);
    }

    return this;
  }

  public selectSecondPaymentCondition({ secondPaymentCondition }: { secondPaymentCondition?: any }): Payment {
    if (secondPaymentCondition) {
      this.setPaymentMethodField(EPaymentMethod.creditCard, 'second_payment_condition', secondPaymentCondition);
    }

    return this;
  }
}
